import { DownloadIcon, EditIcon } from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Button,
  Center,
  Container,
  Flex,
  Heading,
  IconButton,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useAuth } from "../../../contexts/Auth";
import { usePremium } from "../../../contexts/Premium";
import { Logo } from "../../../navigation/Logo";
import {
  cancelSubscription,
  getBilling,
  resumeSubscription,
} from "../../../services/userApi";
import { trackEvent } from "../../../shared/analytics";
import Spinner from "../../../shared/components/Spinner";
import PaymentMethodModal from "../components/PaymentMethodModal";

const Billing = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { authData } = useAuth();
  const { isPremium } = usePremium();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data, isSuccess, isLoading } = useQuery(["billing"], getBilling, {
    refetchOnWindowFocus: false,
  });
  const cancelMutation = useMutation(() => {
    return cancelSubscription();
  });
  const resumeMutation = useMutation(() => {
    return resumeSubscription();
  });

  const onClickCancel = (endDate) => {
    if (window.confirm("Are you sure you want to cancel your subscription?")) {
      cancelMutation
        .mutateAsync()
        .then(() => {
          trackEvent("subscription_cancelled", { store: "stripe" });
          // Refetch billing info
          queryClient.refetchQueries(["billing"]);
          // toast success
          toast({
            title: "Subscription cancelled",
            description: `Your subscription will stop on ${endDate}.`,
            status: "success",
            duration: 5000,
            isClosable: true,
          });
        })
        .catch((error) => {
          // toast error
          toast({
            title: "Error cancelling subscription",
            description: error.message,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        });
    }
  };

  const onClickResume = () => {
    if (window.confirm("Would you like to resume your subscription?")) {
      resumeMutation
        .mutateAsync()
        .then(() => {
          trackEvent("subscription_resumed", { store: "stripe" });
          // Refetch billing info
          queryClient.refetchQueries(["billing"]);
          // toast success
          toast({
            title: "Subscription resumed",
            description: `Your subscription has resumed.`,
            status: "success",
            duration: 5000,
            isClosable: true,
          });
        })
        .catch((error) => {
          // toast error
          toast({
            title: "Error resuming subscription",
            description: error.message,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        });
    }
  };

  if (isLoading) return <Spinner />;

  return (
    <Container>
      <Box width="100%" py="12">
        {!!isPremium && authData.subscriptionPlatform === "stripe" && (
          <VStack spacing="6" alignItems="stretch">
            {/* Subscription Status */}
            <Box bg="gray.700" borderRadius="lg" p="6">
              <Heading as="h2" size="lg" marginBottom="16px">
                Your Subscription
              </Heading>
              {isSuccess && data && (
                <Stack spacing="16px">
                  <Text>
                    Plan:{" "}
                    <strong>
                      Redwing Premium{" "}
                      {data?.subscription?.price ===
                        process.env.REACT_APP_STRIPE_MONTHLY_PRICE_ID &&
                        "Monthly"}
                      {data?.subscription?.price ===
                        process.env.REACT_APP_STRIPE_YEARLY_PRICE_ID &&
                        "Yearly"}
                      {!!authData.premiumLifetime && "Lifetime"}
                    </strong>
                  </Text>
                  <Text>
                    {data?.subscription?.status === "active" &&
                    !data?.subscription?.onGracePeriod
                      ? "Renew on "
                      : "Active until "}{" "}
                    {data?.subscription?.nextBillingDate}
                  </Text>
                  <Flex>
                    {/* <Button colorScheme="blue" marginRight="4" color="white">
                    Switch to Yearly (save 16%)
                  </Button>
                  <Spacer /> */}
                    {data?.subscription?.status === "active" &&
                      !data?.subscription?.onGracePeriod && (
                        <Button
                          colorScheme="red"
                          variant="outline"
                          onClick={() =>
                            onClickCancel(data?.subscription?.nextBillingDate)
                          }
                        >
                          Cancel Subscription
                        </Button>
                      )}

                    {data?.subscription?.status === "active" &&
                      data?.subscription?.onGracePeriod && (
                        <Button
                          colorScheme="red"
                          variant="outline"
                          onClick={() => onClickResume()}
                        >
                          Resume Subscription
                        </Button>
                      )}
                  </Flex>
                </Stack>
              )}
            </Box>

            {/* Payment Methods */}
            <Box bg="gray.700" borderRadius="lg" p="6">
              <Heading as="h2" size="lg" marginBottom="16px">
                Payment Methods
              </Heading>
              {isSuccess && data && (
                <Table variant="simple">
                  <Tbody>
                    <Tr>
                      <Td p="3" pl="0">
                        <Text textTransform="capitalize">
                          {data?.card.type}
                        </Text>
                      </Td>
                      <Td p="3" minWidth="190px">
                        <code>**** **** **** {data?.card.lastFour}</code>
                      </Td>
                      <Td p="3" minWidth="100px">
                        Exp. {data?.card.expMonth}/{data?.card.expYear}
                      </Td>
                      <Td p="3" pr="0" maxW="60px">
                        <IconButton
                          aria-label="Edit payment method"
                          icon={<EditIcon />}
                          onClick={() => onOpen()}
                        />
                      </Td>
                    </Tr>
                  </Tbody>
                </Table>
              )}
            </Box>
            <PaymentMethodModal isOpen={isOpen} onClose={onClose} />

            {/* Bills */}
            <Box bg="gray.700" borderRadius="lg" p="6">
              <Heading as="h2" size="lg" marginBottom="16px">
                Invoices
              </Heading>
              {isSuccess && data && (
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Status</Th>
                      <Th>Date</Th>
                      <Th>Amount</Th>
                      <Th>&nbsp;</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data?.invoices?.map((invoice) => (
                      <Tr key={invoice.id}>
                        <Td>
                          <Badge bg="green.500">{invoice.status}</Badge>
                        </Td>
                        <Td>{invoice.date}</Td>
                        <Td>
                          <Text textTransform="uppercase">
                            {invoice.currency} {invoice.total / 100}
                          </Text>
                        </Td>
                        <Td>
                          <IconButton
                            aria-label="Download bill"
                            icon={<DownloadIcon />}
                            onClick={() =>
                              window.open(invoice.downloadLink, "_blank")
                            }
                          />
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              )}
            </Box>
          </VStack>
        )}
        {!!isPremium && authData.subscriptionPlatform === "revenue_cat" && (
          <VStack spacing="6" alignItems="stretch">
            <Box bg="gray.700" borderRadius="lg" p="6">
              <Heading as="h2" size="lg" marginBottom="16px">
                Your Subscription
              </Heading>
              <Stack spacing="16px">
                {authData.premiumLifetime ? (
                  <Flex>
                    <Logo />
                    <Center px="6">
                      <strong>Lifetime premium</strong>, enjoy Redwing!
                    </Center>
                  </Flex>
                ) : (
                  <Text>
                    You can manage your billing information via the Redwing
                    mobile app.
                  </Text>
                )}
              </Stack>
            </Box>
          </VStack>
        )}
      </Box>
    </Container>
  );
};

export default Billing;
